import { memo } from 'react';

import { UpsellFlashIconButton } from '~/components/SharePrivateClipModalV2/UpsellFlashIconButton';
import { SharePermissionSection } from '~/components/SharePrivateContentModal/SharePrivateContentSettings/SharePermissionSection';
import { type SharePermissionSectionContentProps } from '~/components/SharePrivateContentModal/SharePrivateContentSettings/types';
import { SharePrivateContentPermissionToggle } from '~/components/SharePrivateContentModal/shared/SharePrivateContentPermissionToggle';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';

type ShareCommentsPermissionsProps = {
  canViewDiscussions: boolean;
  canManageDiscussions: boolean;
} & SharePermissionSectionContentProps;

export const ShareCommentsPermissions = memo(
  ({
    onPermissionUpdated,
    shortIdInfo,
    isLoading,
    canViewDiscussions,
    canManageDiscussions,
  }: ShareCommentsPermissionsProps) => {
    const { commentsEnabled, canManageCommentsEnabled } = useShareSettings();

    return (
      <SharePermissionSection title="Comments">
        <SharePrivateContentPermissionToggle
          label="Show comments"
          id="canViewDiscussions"
          isLoading={isLoading}
          checked={shortIdInfo.permissions.asset.canViewDiscussions}
          onCheckedChange={(checked) =>
            onPermissionUpdated({
              asset: {
                canViewDiscussions: checked,
                canManageDiscussions: checked ? shortIdInfo.permissions.asset.canManageDiscussions : false,
              },
            })
          }
          disabled={
            !canViewDiscussions ||
            (shortIdInfo.permissions.asset.canManageDiscussions && !canManageDiscussions) ||
            !commentsEnabled
          }
        >
          {!commentsEnabled && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>}
        </SharePrivateContentPermissionToggle>
        <SharePrivateContentPermissionToggle
          label="Allow commenting"
          id="canManageDiscussions"
          isLoading={isLoading}
          checked={shortIdInfo.permissions.asset.canManageDiscussions}
          onCheckedChange={(checked) =>
            onPermissionUpdated({
              asset: {
                canManageDiscussions: checked,
              },
            })
          }
          disabled={
            !canManageDiscussions || !shortIdInfo.permissions.asset.canViewDiscussions || !canManageCommentsEnabled
          }
        >
          {!canManageCommentsEnabled && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>}
        </SharePrivateContentPermissionToggle>
      </SharePermissionSection>
    );
  },
);

ShareCommentsPermissions.displayName = 'ShareCommentsPermissions';
